<template>
  <div class="strategy-app animate-fadeIn">
    <!-- 顶部导航 -->
    <header class="app-header animate-slideDown">
      <div class="header-left">
        <button class="btn-back-home animate-scaleIn" @click="goToHome" title="返回首页">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
            <polyline points="9 22 9 12 15 12 15 22"></polyline>
          </svg>
          <span>首页</span>
        </button>
        <div class="logo">
          <span class="logo-icon">◈</span>
          <span class="logo-text">StrategyOS</span>
        </div>
      </div>
      <div class="user-info">
        <span class="status-dot running animate-pulse"></span>
        <span>在线</span>
        <div class="avatar">U</div>
      </div>
    </header>

    <!-- 主内容区 -->
    <div class="main-container">
      <!-- 左侧策略列表 -->
      <aside class="strategy-sidebar" :class="{ collapsed: sidebarCollapsed }">
        <div class="sidebar-header">
          <h3>我的策略</h3>
          <button class="btn-icon" @click="showCreateModal = true" title="新建策略">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </button>
        </div>
        
        <div class="strategy-list">
          <div 
            v-for="strategy in strategies" 
            :key="strategy.id"
            class="strategy-card"
            :class="{ 
              active: currentStrategy?.id === strategy.id,
              running: strategy.status === 1,
              stopped: strategy.status === 0
            }"
            @click="selectStrategy(strategy)"
          >
            <div class="strategy-card-header">
              <span class="strategy-name">{{ strategy.strategy_name }}</span>
            </div>
            <div class="strategy-metrics">
              <div class="metric">
                <span class="metric-label">权益</span>
                <span class="metric-value" :class="getProfitClass(strategy)">
                  ¥{{ formatNumber(strategy.total_assets) }}
                </span>
              </div>
              <div class="metric">
                <span class="metric-label">盈亏</span>
                <span class="metric-value" :class="getProfitClass(strategy)">
                  {{ getProfitRate(strategy) }}%
                </span>
              </div>
            </div>
            <div class="strategy-progress">
              <div class="progress-bar" :style="{ width: getPositionPercent(strategy) + '%' }"></div>
            </div>
          </div>
        </div>

        <div class="sidebar-footer">
          <button class="btn-toggle" @click="sidebarCollapsed = !sidebarCollapsed">
            {{ sidebarCollapsed ? '展开' : '收起' }}
          </button>
        </div>
      </aside>

      <!-- 右侧策略面板 -->
      <main class="strategy-panel" v-if="currentStrategy">
        <!-- 面板头部 -->
        <div class="panel-header">
          <div class="panel-title">
            <h2>{{ currentStrategy.strategy_name }}</h2>
          </div>
          <div class="panel-actions">
            <button 
              class="btn-trade"
              @click="enterTrading"
            >
              开始交易
            </button>
            <button class="btn-icon" @click="openSettings" title="策略设置">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- 策略表现概览 -->
        <div class="capital-overview">
          <div class="capital-card primary animate-scaleIn" style="animation-delay: 0.1s;">
            <div class="capital-label">总权益</div>
            <div class="capital-value">¥{{ formatNumber(currentStrategy.total_assets) }}</div>
            <div class="capital-change" :class="getProfitClass(currentStrategy)">
              {{ formatPnl(getProfitAmount(currentStrategy)) }}
              ({{ getProfitRate(currentStrategy) }}%)
            </div>
          </div>
          <div class="capital-card">
            <div class="capital-label">可用资金</div>
            <div class="capital-value">¥{{ formatNumber(currentStrategy.balance) }}</div>
          </div>
          <div class="capital-card">
            <div class="capital-label">冻结资金</div>
            <div class="capital-value">¥{{ formatNumber(currentStrategy.frozen_balance) }}</div>
          </div>
          <div class="capital-card">
            <div class="capital-label">初始资金</div>
            <div class="capital-value">¥{{ formatNumber(currentStrategy.init_capital) }}</div>
          </div>
        </div>

        <!-- 资金曲线图表 -->
        <div class="equity-curve-section animate-scaleIn" style="animation-delay: 0.2s;">
          <div class="section-header">
            <h4 class="section-title">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
              </svg>
              资金曲线
            </h4>
            <div class="equity-stats" v-if="equityStats">
              <span class="stat-item">
                总交易: {{ equityStats.total_trades }}笔
              </span>
              <span class="stat-item">
                盈利: {{ equityStats.winning_trades }}笔
              </span>
              <span class="stat-item">
                亏损: {{ equityStats.losing_trades }}笔
              </span>
              <span class="stat-item" :class="equityStats.total_pnl >= 0 ? 'profit' : 'loss'">
                总盈亏: {{ formatPnl(equityStats.total_pnl) }}
              </span>
              <span class="stat-item" v-if="equityStats.profit_loss_ratio">
                盈亏比: {{ equityStats.profit_loss_ratio.toFixed(2) }}
              </span>
            </div>
          </div>
          <div class="equity-chart-container">
            <div v-if="loadingEquityCurve" class="loading-overlay">
              <div class="spinner"></div>
              <span>加载中...</span>
            </div>
            <canvas ref="equityChart" v-show="!loadingEquityCurve && equityCurve.length > 0"></canvas>
            <div v-if="!loadingEquityCurve && equityCurve.length <= 1" class="empty-chart">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
              </svg>
              <p>暂无交易记录</p>
              <p class="hint">开始交易后将自动记录资金变化</p>
            </div>
          </div>
          <!-- 交易记录列表 -->
          <div class="trade-records" v-if="equityCurve.length > 1">
            <div class="records-header">
              <h5>交易记录</h5>
            </div>
            <div class="records-list">
              <div 
                v-for="(point, index) in equityCurve.slice(1)" 
                :key="index"
                class="record-item"
                :class="point.pnl >= 0 ? 'profit' : 'loss'"
              >
                <div class="record-time">{{ formatTime(point.time) }}</div>
                <div class="record-desc">{{ point.description }}</div>
                <div class="record-pnl">
                  <span class="pnl-value">{{ formatPnl(point.pnl) }}</span>
                  <span class="pnl-pct">({{ point.pnl_pct.toFixed(2) }}%)</span>
                </div>
                <div class="record-equity">权益: ¥{{ formatNumber(point.equity) }}</div>
              </div>
            </div>
          </div>
        </div>

      </main>

      <!-- 空状态 -->
      <div class="empty-panel" v-else>
        <div class="empty-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
            <line x1="8" y1="21" x2="16" y2="21"></line>
            <line x1="12" y1="17" x2="12" y2="21"></line>
          </svg>
        </div>
        <h3>选择一个策略开始交易</h3>
        <p>或创建新策略</p>
        <button class="btn-primary" @click="showCreateModal = true">新建策略</button>
      </div>
    </div>

    <!-- 创建策略弹窗 -->
    <div class="modal-overlay" v-if="showCreateModal" @click.self="showCreateModal = false">
      <div class="modal-container">
        <div class="modal-header">
          <h3>新建交易策略</h3>
          <button class="btn-close" @click="showCreateModal = false">×</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="createStrategy">
            <div class="form-group">
              <label>策略名称</label>
              <input v-model="newStrategy.strategy_name" type="text" placeholder="如：焦煤短线策略" required>
            </div>

            <div class="form-group">
              <label>初始资金 (¥)</label>
              <input v-model.number="newStrategy.init_capital" type="number" placeholder="100000" required>
            </div>
            
            <div class="form-actions">
              <button type="button" class="btn-secondary" @click="showCreateModal = false">取消</button>
              <button type="submit" class="btn-primary">创建策略</button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- 设置弹窗 -->
    <div class="modal-overlay" v-if="showSettings" @click.self="showSettings = false">
      <div class="modal-container small">
        <div class="modal-header">
          <h3>策略设置</h3>
          <button class="btn-close" @click="showSettings = false">×</button>
        </div>
        <div class="modal-body">
          <div class="settings-info">
            <p><strong>策略ID:</strong> {{ currentStrategy.id }}</p>
            <p><strong>创建时间:</strong> {{ currentStrategy.created_at }}</p>
          </div>
          <div class="form-actions">
            <button class="btn-danger" @click="deleteStrategy">删除策略</button>
            <button class="btn-secondary" @click="showSettings = false">关闭</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { quantApi } from '../api/quant'

export default {
  name: 'StrategyManager',
  data() {
    return {
      sidebarCollapsed: false,
      showCreateModal: false,
      showSettings: false,
      currentPeriod: '1min',
      currentStrategy: null,
      loading: false,
      userId: 1, // 默认用户ID，实际应该从登录状态获取
      newStrategy: {
        strategy_name: '',
        init_capital: 100000,
        description: ''
      },
      strategies: [],
      equityCurve: [],
      equityStats: null,
      loadingEquityCurve: false
    }
  },
  async created() {
    await this.loadStrategies()
    if (this.strategies.length > 0) {
      this.currentStrategy = this.strategies[0]
      await this.loadEquityCurve()
    }
  },
  watch: {
    async currentStrategy(newVal) {
      if (newVal) {
        await this.loadEquityCurve()
      }
    }
  },
  methods: {
    goToHome() {
      this.$router.push('/')
    },
    
    openSettings() {
      if (!this.currentStrategy) {
        alert('请先选择一个策略')
        return
      }
      this.showSettings = true
    },
    
    async loadStrategies() {
      this.loading = true
      try {
        const result = await quantApi.getStrategies(this.userId)
        this.strategies = result.strategies || []
      } catch (error) {
        this.strategies = []
      } finally {
        this.loading = false
      }
    },

    async selectStrategy(strategy) {
      // 清空旧的资金曲线数据和图表
      this.equityCurve = []
      this.equityStats = null
      
      // 清空canvas
      this.clearEquityChart()
      
      // 更新当前策略
      this.currentStrategy = strategy
      
      // 异步加载数据
      await this.loadStrategyData(strategy.id)
      await this.loadEquityCurve()
    },
    
    clearEquityChart() {
      const canvas = this.$refs.equityChart
      if (!canvas) return
      
      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, canvas.width, canvas.height)
    },

    async loadStrategyData(strategyId) {
      try {
        // 获取策略详情
        const strategy = await quantApi.getStrategyDetail(strategyId)
        this.positions = strategy.positions || []
        this.orders = strategy.orders || []
      } catch (error) {
      }
    },

    async loadEquityCurve() {
      if (!this.currentStrategy) return
      
      this.loadingEquityCurve = true
      try {
        const result = await quantApi.getStrategyEquityCurve(this.currentStrategy.id)
        this.equityCurve = result.equity_curve || []
        this.equityStats = result.stats || null
        
        // 绘制图表
        this.$nextTick(() => {
          if (this.equityCurve.length > 1) {
            this.drawEquityChart()
          }
        })
      } catch (error) {
        this.equityCurve = []
        this.equityStats = null
      } finally {
        this.loadingEquityCurve = false
      }
    },

    drawEquityChart() {
      const canvas = this.$refs.equityChart
      if (!canvas || this.equityCurve.length < 2) return
      
      const ctx = canvas.getContext('2d')
      const container = canvas.parentElement
      
      // 设置画布尺寸
      const dpr = window.devicePixelRatio || 1
      const rect = container.getBoundingClientRect()
      canvas.width = rect.width * dpr
      canvas.height = 300 * dpr
      canvas.style.width = rect.width + 'px'
      canvas.style.height = '300px'
      ctx.scale(dpr, dpr)
      
      const width = rect.width
      const height = 300
      const padding = { top: 20, right: 20, bottom: 40, left: 80 }
      const chartWidth = width - padding.left - padding.right
      const chartHeight = height - padding.top - padding.bottom
      
      // 清空画布
      ctx.clearRect(0, 0, width, height)
      
      // 计算数据范围
      const equities = this.equityCurve.map(p => p.equity)
      const minEquity = Math.min(...equities)
      const maxEquity = Math.max(...equities)
      const equityRange = maxEquity - minEquity || 1
      
      // 绘制背景网格
      ctx.strokeStyle = '#2a2e39'
      ctx.lineWidth = 1
      
      // 横向网格线
      for (let i = 0; i <= 5; i++) {
        const y = padding.top + (chartHeight / 5) * i
        ctx.beginPath()
        ctx.moveTo(padding.left, y)
        ctx.lineTo(width - padding.right, y)
        ctx.stroke()
        
        // Y轴标签
        const value = maxEquity - (equityRange / 5) * i
        ctx.fillStyle = '#848484'
        ctx.font = '12px sans-serif'
        ctx.textAlign = 'right'
        ctx.fillText('¥' + this.formatNumber(value), padding.left - 10, y + 4)
      }
      
      // 绘制资金曲线
      ctx.beginPath()
      ctx.strokeStyle = '#00d4aa'
      ctx.lineWidth = 2
      
      this.equityCurve.forEach((point, index) => {
        const x = padding.left + (chartWidth / (this.equityCurve.length - 1)) * index
        const y = padding.top + chartHeight - ((point.equity - minEquity) / equityRange) * chartHeight
        
        if (index === 0) {
          ctx.moveTo(x, y)
        } else {
          ctx.lineTo(x, y)
        }
      })
      
      ctx.stroke()
      
      // 绘制数据点
      this.equityCurve.forEach((point, index) => {
        const x = padding.left + (chartWidth / (this.equityCurve.length - 1)) * index
        const y = padding.top + chartHeight - ((point.equity - minEquity) / equityRange) * chartHeight
        
        ctx.beginPath()
        ctx.arc(x, y, 4, 0, Math.PI * 2)
        ctx.fillStyle = point.pnl >= 0 ? '#00d4aa' : '#ff6b6b'
        ctx.fill()
      })
      
      // 绘制初始资金线
      const initEquity = this.equityCurve[0].equity
      const initY = padding.top + chartHeight - ((initEquity - minEquity) / equityRange) * chartHeight
      ctx.beginPath()
      ctx.strokeStyle = '#5a5e69'
      ctx.lineWidth = 1
      ctx.setLineDash([5, 5])
      ctx.moveTo(padding.left, initY)
      ctx.lineTo(width - padding.right, initY)
      ctx.stroke()
      ctx.setLineDash([])
      
      // X轴标签（时间）
      ctx.fillStyle = '#848484'
      ctx.font = '11px sans-serif'
      ctx.textAlign = 'center'
      
      const labelInterval = Math.ceil(this.equityCurve.length / 6)
      this.equityCurve.forEach((point, index) => {
        if (index % labelInterval === 0 || index === this.equityCurve.length - 1) {
          const x = padding.left + (chartWidth / (this.equityCurve.length - 1)) * index
          const timeStr = this.formatTimeShort(point.time)
          ctx.fillText(timeStr, x, height - 10)
        }
      })
    },

    formatTime(timeStr) {
      if (!timeStr) return '-'
      const date = new Date(timeStr)
      return date.toLocaleString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    formatTimeShort(timeStr) {
      if (!timeStr) return '-'
      const date = new Date(timeStr)
      return `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`
    },

    formatPnl(value) {
      if (!value || value === 0) return '¥0.00'
      const sign = value >= 0 ? '+' : ''
      return sign + '¥' + value.toFixed(2)
    },

    async createStrategy() {
      if (!this.newStrategy.strategy_name.trim()) {
        alert('请输入策略名称')
        return
      }

      try {
        const result = await quantApi.createStrategy({
          user_id: this.userId,
          strategy_name: this.newStrategy.strategy_name.trim(),
          init_capital: this.newStrategy.init_capital,
          description: this.newStrategy.description.trim()
        })
        
        this.showCreateModal = false
        this.resetForm()
        await this.loadStrategies()
        
        // 自动选择新创建的策略
        if (result.strategy) {
          this.currentStrategy = result.strategy
        }
      } catch (error) {
        alert('创建策略失败: ' + (error.response?.data?.detail || error.message))
      }
    },

    async deleteStrategy() {
      if (!this.currentStrategy) return
      
      if (confirm('确定要删除此策略吗？删除后无法恢复！')) {
        try {
          await quantApi.deleteStrategy(this.currentStrategy.id)
          
          // 重新从后端加载策略列表，确保数据一致
          await this.loadStrategies()
          
          // 更新当前策略
          this.currentStrategy = this.strategies.length > 0 ? this.strategies[0] : null
          this.showSettings = false
          
          if (this.currentStrategy) {
            this.loadStrategyData(this.currentStrategy.id)
            this.loadEquityCurve()
          }
        } catch (error) {
          alert('删除策略失败: ' + (error.response?.data?.detail || error.message))
        }
      }
    },

    enterTrading() {
      if (!this.currentStrategy) {
        alert('请先选择一个策略')
        return
      }
      
      // 跳转到交易界面
      this.$router.push(`/trading/${this.currentStrategy.id}`)
    },

    resetForm() {
      this.newStrategy = {
        strategy_name: '',
        init_capital: 100000,
        description: ''
      }
    },

    formatNumber(num) {
      if (num === null || num === undefined) {
        return '0.00'
      }
      return num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    getProfitRate(strategy) {
      if (!strategy || strategy.init_capital === null || strategy.init_capital === undefined) {
        return '0.00'
      }
      const initCapital = parseFloat(strategy.init_capital) || 0
      const totalAssets = parseFloat(strategy.total_assets) || 0
      if (initCapital === 0) return '0.00'
      return ((totalAssets - initCapital) / initCapital * 100).toFixed(2)
    },

    getProfitAmount(strategy) {
      if (!strategy) return 0
      const initCapital = parseFloat(strategy.init_capital) || 0
      const totalAssets = parseFloat(strategy.total_assets) || 0
      return totalAssets - initCapital
    },

    getProfitClass(strategy) {
      if (!strategy) return 'loss'
      const initCapital = parseFloat(strategy.init_capital) || 0
      const totalAssets = parseFloat(strategy.total_assets) || 0
      const rate = totalAssets - initCapital
      return rate >= 0 ? 'profit' : 'loss'
    },

    getPositionPercent(strategy) {
      if (!strategy) return 0
      const frozenBalance = parseFloat(strategy.frozen_balance) || 0
      const totalAssets = parseFloat(strategy.total_assets) || 1
      return Math.min((frozenBalance / totalAssets) * 100, 100)
    },

    getCandleStyle(i) {
      const height = 30 + Math.random() * 70
      const isUp = Math.random() > 0.4
      return {
        height: height + 'px',
        backgroundColor: isUp ? '#10b981' : '#ef4444',
        opacity: 0.6 + Math.random() * 0.4
      }
    }
  }
}
</script>

<style scoped>
/* ===== 设计系统 ===== */
:root {
  --bg-primary: #0a0e1a;
  --bg-secondary: #111827;
  --bg-card: #1f2937;
  --bg-hover: #374151;
  --border-color: #374151;
  --text-primary: #f9fafb;
  --text-secondary: #9ca3af;
  --text-muted: #6b7280;
  --accent-blue: #3b82f6;
  --accent-cyan: #06b6d4;
  --profit-red: #ef4444;
  --loss-green: #10b981;
  --warning-yellow: #f59e0b;
}

/* ===== 基础布局 ===== */
.strategy-app {
  width: 100vw;
  height: 100vh;
  background-color: #0a0e1a;
  background-image: 
    radial-gradient(ellipse at top left, rgba(59, 130, 246, 0.08) 0%, transparent 50%),
    radial-gradient(ellipse at bottom right, rgba(139, 92, 246, 0.06) 0%, transparent 50%);
  color: #f9fafb;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ===== 顶部导航 ===== */
.app-header {
  height: 56px;
  background: rgba(17, 24, 39, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid #374151;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  z-index: 100;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.btn-back-home {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.btn-back-home:hover {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.btn-back-home:active {
  transform: translateY(0);
}

.logo {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;
  font-size: 18px;
  letter-spacing: 0.5px;
}

.logo-icon {
  color: #06b6d4;
  font-size: 24px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: #9ca3af;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 8px #10b981;
}

.avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #06b6d4);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
}

/* ===== 主容器 ===== */
.main-container {
  flex: 1;
  display: flex;
  overflow: hidden;
}

/* ===== 左侧边栏 ===== */
.strategy-sidebar {
  width: 320px;
  background: linear-gradient(180deg, rgba(17, 24, 39, 0.98) 0%, rgba(17, 24, 39, 0.95) 100%);
  border-right: 1px solid rgba(55, 65, 81, 0.6);
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
  position: relative;
  backdrop-filter: blur(10px);
}

.strategy-sidebar::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 1px;
  height: 100%;
  background: linear-gradient(180deg, rgba(59, 130, 246, 0.3), transparent 50%);
}

.strategy-sidebar.collapsed {
  width: 60px;
}

.sidebar-header {
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(55, 65, 81, 0.6);
  background: rgba(17, 24, 39, 0.5);
}

.sidebar-header h3 {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  background: linear-gradient(135deg, #f9fafb, #d1d5db);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.btn-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #374151;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.btn-icon:hover {
  background: #374151;
  color: #f9fafb;
  border-color: #3b82f6;
}

/* ===== 策略卡片 ===== */
.strategy-list {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
}

.strategy-list::-webkit-scrollbar {
  width: 6px;
}

.strategy-list::-webkit-scrollbar-track {
  background: rgba(55, 65, 81, 0.1);
  border-radius: 3px;
}

.strategy-list::-webkit-scrollbar-thumb {
  background: rgba(55, 65, 81, 0.4);
  border-radius: 3px;
}

.strategy-list::-webkit-scrollbar-thumb:hover {
  background: rgba(55, 65, 81, 0.6);
}

.strategy-card {
  background: linear-gradient(135deg, rgba(31, 41, 55, 0.95) 0%, rgba(31, 41, 55, 0.85) 100%);
  border: 1px solid rgba(55, 65, 81, 0.5);
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
}

.strategy-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: transparent;
  transition: all 0.3s ease;
}

.strategy-card::after {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.05), transparent 70%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.strategy-card:hover {
  border-color: rgba(59, 130, 246, 0.4);
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(59, 130, 246, 0.1);
}

.strategy-card:hover::after {
  opacity: 1;
}

.strategy-card.active {
  border-color: rgba(59, 130, 246, 0.6);
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.98) 0%, rgba(30, 58, 95, 0.3) 100%);
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.15), 0 0 0 1px rgba(59, 130, 246, 0.2);
}

.strategy-card.active::before {
  background: linear-gradient(180deg, #3b82f6, #06b6d4);
  box-shadow: 0 0 12px rgba(59, 130, 246, 0.5);
}

.strategy-card.running {
  box-shadow: inset 0 0 30px rgba(16, 185, 129, 0.05);
}

.strategy-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.strategy-name {
  font-weight: 600;
  font-size: 15px;
  color: #f9fafb;
  letter-spacing: 0.3px;
}



.strategy-symbol {
  font-size: 13px;
  color: #9ca3af;
  margin-bottom: 12px;
  font-family: 'Courier New', monospace;
  font-weight: 600;
  letter-spacing: 1px;
}

.strategy-metrics {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
}

.metric {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.metric-label {
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  font-weight: 500;
}

.metric-value {
  font-size: 14px;
  font-weight: 600;
  font-family: 'Courier New', monospace;
  color: #f9fafb;
}

.strategy-progress {
  height: 4px;
  background: rgba(55, 65, 81, 0.5);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 8px;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #06b6d4, #00d4aa);
  border-radius: 2px;
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 0 8px rgba(59, 130, 246, 0.4);
}

.sidebar-footer {
  padding: 12px;
  border-top: 1px solid rgba(55, 65, 81, 0.6);
  background: rgba(17, 24, 39, 0.3);
}

.btn-toggle {
  width: 100%;
  padding: 10px;
  background: linear-gradient(135deg, rgba(55, 65, 81, 0.5) 0%, rgba(55, 65, 81, 0.3) 100%);
  border: 1px solid rgba(55, 65, 81, 0.6);
  color: #9ca3af;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.btn-toggle:hover {
  background: linear-gradient(135deg, rgba(55, 65, 81, 0.7) 0%, rgba(55, 65, 81, 0.5) 100%);
  color: #f9fafb;
  border-color: rgba(59, 130, 246, 0.4);
}

/* ===== 右侧面板 ===== */
.strategy-panel {
  flex: 1;
  overflow-y: auto;
  padding: 32px;
  background: linear-gradient(180deg, rgba(10, 14, 26, 1) 0%, rgba(10, 14, 26, 0.98) 100%);
}

.strategy-panel::-webkit-scrollbar {
  width: 8px;
}

.strategy-panel::-webkit-scrollbar-track {
  background: rgba(55, 65, 81, 0.1);
  border-radius: 4px;
}

.strategy-panel::-webkit-scrollbar-thumb {
  background: rgba(55, 65, 81, 0.4);
  border-radius: 4px;
}

.strategy-panel::-webkit-scrollbar-thumb:hover {
  background: rgba(55, 65, 81, 0.6);
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid rgba(55, 65, 81, 0.6);
  position: relative;
}

.panel-header::before {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 100px;
  height: 2px;
  background: linear-gradient(90deg, #3b82f6, transparent);
}

.panel-title {
  display: flex;
  align-items: center;
  gap: 16px;
}

.panel-title h2 {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.5px;
  background: linear-gradient(135deg, #f9fafb, #d1d5db);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.panel-tag {
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  background: rgba(55, 65, 81, 0.3);
  color: #9ca3af;
  letter-spacing: 0.5px;
  border: 1px solid rgba(55, 65, 81, 0.3);
}

.panel-tag.auto {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(59, 130, 246, 0.1) 100%);
  color: #3b82f6;
  border-color: rgba(59, 130, 246, 0.3);
}

.panel-tag.manual {
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(245, 158, 11, 0.1) 100%);
  color: #f59e0b;
  border-color: rgba(245, 158, 11, 0.3);
}

.panel-actions {
  display: flex;
  gap: 16px;
  align-items: center;
}

.btn-status {
  padding: 10px 24px;
  border-radius: 10px;
  border: none;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.btn-start {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.3);
}

.btn-start:hover {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  box-shadow: 0 6px 24px rgba(16, 185, 129, 0.4);
  transform: translateY(-1px);
}

.btn-trade {
  padding: 10px 24px;
  border-radius: 10px;
  border: none;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.3);
}

.btn-trade:hover:not(:disabled) {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  box-shadow: 0 6px 24px rgba(59, 130, 246, 0.4);
  transform: translateY(-1px);
}

.btn-trade:disabled {
  background: linear-gradient(135deg, rgba(55, 65, 81, 0.5) 0%, rgba(55, 65, 81, 0.3) 100%);
  color: #6b7280;
  cursor: not-allowed;
  box-shadow: none;
}

.btn-stop {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(239, 68, 68, 0.05) 100%);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.3);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.1);
}

.btn-stop:hover {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.25) 0%, rgba(239, 68, 68, 0.15) 100%);
  box-shadow: 0 6px 20px rgba(239, 68, 68, 0.2);
}

/* ===== 资金概览 ===== */
.capital-overview {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.capital-card {
  background: linear-gradient(135deg, rgba(31, 41, 55, 0.98) 0%, rgba(31, 41, 55, 0.9) 100%);
  border: 1px solid rgba(55, 65, 81, 0.5);
  border-radius: 16px;
  padding: 24px;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.capital-card::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.08), transparent 60%);
  transition: all 0.5s ease;
}

.capital-card::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 80px;
  height: 80px;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.15), transparent 70%);
  transition: all 0.3s ease;
}

.capital-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3);
}

.capital-card:hover::before {
  transform: rotate(180deg);
}

.capital-card.primary {
  border-color: rgba(59, 130, 246, 0.6);
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.98) 0%, rgba(30, 58, 95, 0.3) 100%);
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.15), 0 0 0 1px rgba(59, 130, 246, 0.2);
}

.capital-card.primary::before {
  background: radial-gradient(circle, rgba(59, 130, 246, 0.12), transparent 60%);
}

.capital-label {
  font-size: 12px;
  color: #9ca3af;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 600;
  position: relative;
  z-index: 1;
}

.capital-value {
  font-size: 26px;
  font-weight: 700;
  font-family: 'Courier New', monospace;
  color: #f9fafb;
  position: relative;
  z-index: 1;
  letter-spacing: 0.5px;
}

.capital-change {
  font-size: 14px;
  font-weight: 600;
  margin-top: 8px;
  font-family: 'Courier New', monospace;
  position: relative;
  z-index: 1;
}

/* ===== 开始模拟交易区域 ===== */
.start-trading-section {
  margin-bottom: 24px;
}

.start-trading-card {
  background: linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%);
  border: 1px solid #3b82f6;
  border-radius: 16px;
  padding: 40px;
  text-align: center;
  box-shadow: 0 0 40px rgba(59, 130, 246, 0.2);
}

.start-trading-icon {
  margin-bottom: 20px;
  color: #3b82f6;
}

.start-trading-card h3 {
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 12px 0;
}

.start-trading-card p {
  font-size: 14px;
  color: #9ca3af;
  margin: 0 0 24px 0;
  line-height: 1.6;
}

.btn-start-trading {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 32px;
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(59, 130, 246, 0.4);
}

.btn-start-trading:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(59, 130, 246, 0.6);
}

.btn-start-trading:active:not(:disabled) {
  transform: translateY(0);
}

.btn-start-trading:disabled {
  background: #374151;
  color: #6b7280;
  cursor: not-allowed;
  box-shadow: none;
}

/* ===== 资金曲线区域 ===== */
.equity-curve-section {
  background: linear-gradient(135deg, rgba(31, 41, 55, 0.98) 0%, rgba(31, 41, 55, 0.9) 100%);
  border: 1px solid rgba(55, 65, 81, 0.5);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
}

.equity-curve-section::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle, rgba(139, 92, 246, 0.05), transparent 60%);
  pointer-events: none;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  position: relative;
  z-index: 1;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 600;
  color: #f9fafb;
  margin: 0;
}

.section-title svg {
  color: #3b82f6;
}

.equity-stats {
  display: flex;
  gap: 20px;
  font-size: 13px;
}

.stat-item {
  color: #9ca3af;
  padding: 6px 12px;
  background: rgba(55, 65, 81, 0.3);
  border-radius: 6px;
  font-weight: 500;
}

.stat-item.profit {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
}

.stat-item.loss {
  color: #10b981;
  background: rgba(16, 185, 129, 0.1);
}

.equity-chart-container {
  position: relative;
  background: rgba(17, 24, 39, 0.8);
  border-radius: 12px;
  padding: 20px;
  min-height: 320px;
  border: 1px solid rgba(55, 65, 81, 0.3);
}

.loading-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: #9ca3af;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(55, 65, 81, 0.3);
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  box-shadow: 0 0 20px rgba(59, 130, 246, 0.2);
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-chart {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: #6b7280;
}

.empty-chart svg {
  margin-bottom: 12px;
  opacity: 0.5;
}

.empty-chart p {
  margin: 4px 0;
}

.empty-chart .hint {
  font-size: 12px;
  color: #4b5563;
}

/* ===== 交易记录列表 ===== */
.trade-records {
  margin-top: 20px;
  border-top: 1px solid #374151;
  padding-top: 20px;
}

.records-header h5 {
  font-size: 14px;
  font-weight: 600;
  color: #9ca3af;
  margin: 0 0 12px 0;
}

.records-list {
  max-height: 400px;
  overflow-y: auto;
  border-radius: 12px;
  border: 1px solid rgba(55, 65, 81, 0.5);
  background: rgba(17, 24, 39, 0.6);
}

.records-list::-webkit-scrollbar {
  width: 6px;
}

.records-list::-webkit-scrollbar-track {
  background: rgba(55, 65, 81, 0.1);
  border-radius: 3px;
}

.records-list::-webkit-scrollbar-thumb {
  background: rgba(55, 65, 81, 0.4);
  border-radius: 3px;
}

.record-item {
  display: grid;
  grid-template-columns: 120px 1fr 150px 150px;
  gap: 16px;
  padding: 14px 18px;
  background: rgba(17, 24, 39, 0.4);
  margin-bottom: 8px;
  border-left: 3px solid transparent;
  align-items: center;
  transition: all 0.2s ease;
  backdrop-filter: blur(4px);
}

.record-item:last-child {
  margin-bottom: 0;
}

.record-item.profit {
  border-left-color: #ef4444;
  background: rgba(239, 68, 68, 0.05);
}

.record-item.loss {
  border-left-color: #10b981;
  background: rgba(16, 185, 129, 0.05);
}

.record-item:hover {
  background: rgba(59, 130, 246, 0.08);
}

.record-time {
  font-size: 12px;
  color: #9ca3af;
  font-family: 'Courier New', monospace;
  font-weight: 500;
}

.record-desc {
  font-size: 13px;
  color: #f9fafb;
  font-weight: 500;
}

.record-pnl {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.pnl-value {
  font-size: 14px;
  font-weight: 600;
  font-family: 'Courier New', monospace;
}

.record-item.profit .pnl-value {
  color: #ef4444;
}

.record-item.loss .pnl-value {
  color: #10b981;
}

.pnl-pct {
  font-size: 11px;
  color: #6b7280;
}

.record-equity {
  font-size: 12px;
  color: #9ca3af;
  text-align: right;
  font-family: 'Courier New', monospace;
  font-weight: 500;
}

/* ===== 风控区域 ===== */
.risk-section {
  background: linear-gradient(135deg, rgba(31, 41, 55, 0.98) 0%, rgba(31, 41, 55, 0.9) 100%);
  border: 1px solid rgba(55, 65, 81, 0.5);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
}

.risk-section::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle, rgba(239, 68, 68, 0.03), transparent 60%);
  pointer-events: none;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #f9fafb;
  margin: 0 0 20px 0;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  position: relative;
  z-index: 1;
}

.section-title svg {
  color: #ef4444;
}

.risk-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  position: relative;
  z-index: 1;
}

.risk-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  background: rgba(17, 24, 39, 0.8);
  border-radius: 12px;
  border: 1px solid rgba(55, 65, 81, 0.5);
  transition: all 0.2s ease;
}

.risk-item:hover {
  border-color: rgba(239, 68, 68, 0.3);
  background: rgba(17, 24, 39, 0.9);
  transform: translateY(-2px);
}

.risk-label {
  font-size: 12px;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
}

.risk-value {
  font-size: 16px;
  font-weight: 600;
  color: #f9fafb;
  font-family: 'Courier New', monospace;
}

/* ===== 图表区域 ===== */
.chart-section {
  background: #1f2937;
  border: 1px solid #374151;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.chart-controls {
  display: flex;
  gap: 4px;
}

.btn-period {
  padding: 4px 12px;
  border-radius: 6px;
  border: 1px solid #374151;
  background: transparent;
  color: #6b7280;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-period:hover {
  border-color: #4b5563;
  color: #9ca3af;
}

.btn-period.active {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.chart-placeholder {
  height: 300px;
  background: #111827;
  border-radius: 8px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-mock {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: space-around;
  padding: 0 20px;
  opacity: 0.3;
}

.candlestick {
  width: 8px;
  border-radius: 2px 2px 0 0;
  transition: all 0.3s;
}

.chart-overlay {
  position: relative;
  z-index: 2;
  text-align: center;
  color: #6b7280;
}

.chart-overlay span {
  display: block;
  font-size: 16px;
  margin-bottom: 8px;
  color: #9ca3af;
}

.chart-overlay p {
  font-size: 12px;
  font-family: 'Courier New', monospace;
  color: #374151;
}

/* ===== 数据表格区域 ===== */
.data-sections {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.data-section {
  background: #1f2937;
  border: 1px solid #374151;
  border-radius: 12px;
  padding: 20px;
}

.data-table {
  display: flex;
  flex-direction: column;
}

.table-header {
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1fr 1fr;
  gap: 12px;
  padding: 12px;
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #374151;
}

.table-row {
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1fr 1fr;
  gap: 12px;
  padding: 12px;
  font-size: 13px;
  border-bottom: 1px solid #374151;
  transition: background 0.2s;
}

.table-row:hover {
  background: #374151;
}

.table-row span {
  display: flex;
  align-items: center;
}

.long {
  color: #10b981;
  font-weight: 600;
}

.short {
  color: #ef4444;
  font-weight: 600;
}

.profit {
  color: #ef4444;
}

.loss {
  color: #10b981;
}

.status-badge {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
}

.status-badge.filled {
  background: rgba(16, 185, 129, 0.15);
  color: #10b981;
}

.status-badge.pending {
  background: rgba(245, 158, 11, 0.15);
  color: #f59e0b;
}

.empty-state {
  padding: 32px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
}

/* ===== 空状态 ===== */
.empty-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  position: relative;
}

.empty-panel::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.05), transparent 50%);
  pointer-events: none;
}

.empty-icon {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(31, 41, 55, 0.98), rgba(55, 65, 81, 0.5));
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 32px;
  color: #6b7280;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  position: relative;
  opacity: 1;
}

.empty-icon::before {
  content: '';
  position: absolute;
  inset: -3px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.3), rgba(139, 92, 246, 0.1));
  z-index: -1;
}

.empty-panel h3 {
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 12px;
  color: #f9fafb;
  letter-spacing: -0.5px;
}

.empty-panel p {
  font-size: 15px;
  color: #9ca3af;
  margin: 0 0 32px;
  line-height: 1.6;
}

/* ===== 弹窗 ===== */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s;
}

.modal-container {
  background: linear-gradient(135deg, rgba(31, 41, 55, 0.98) 0%, rgba(31, 41, 55, 0.95) 100%);
  border: 1px solid rgba(55, 65, 81, 0.6);
  border-radius: 20px;
  width: 100%;
  max-width: 540px;
  max-height: 90vh;
  overflow-y: auto;
  animation: scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(20px);
  position: relative;
}

.modal-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #3b82f6, #06b6d4, #10b981);
  border-radius: 20px 20px 0 0;
}

.modal-container.small {
  max-width: 420px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #374151;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
}

.btn-close {
  background: none;
  border: none;
  color: #6b7280;
  font-size: 24px;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s;
}

.btn-close:hover {
  background: #374151;
  color: #f9fafb;
}

.modal-body {
  padding: 24px;
}

/* ===== 表单 ===== */
.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  font-size: 13px;
  color: #9ca3af;
  margin-bottom: 6px;
  font-weight: 500;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 10px 14px;
  background: #111827;
  border: 1px solid #374151;
  border-radius: 8px;
  color: #f9fafb;
  font-size: 14px;
  transition: all 0.2s;
  box-sizing: border-box;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-section {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #374151;
}

.form-section h4 {
  margin: 0 0 16px 0;
  font-size: 14px;
  color: #9ca3af;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #374151;
}

.btn-primary {
  padding: 10px 24px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary:hover {
  background: #2563eb;
  box-shadow: 0 0 20px rgba(59, 130, 246, 0.3);
}

.btn-secondary {
  padding: 10px 24px;
  background: transparent;
  color: #9ca3af;
  border: 1px solid #374151;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary:hover {
  border-color: #6b7280;
  color: #f9fafb;
}

.btn-danger {
  padding: 10px 24px;
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-danger:hover {
  background: rgba(239, 68, 68, 0.2);
}

.settings-info {
  margin-bottom: 20px;
}

.settings-info p {
  margin: 8px 0;
  font-size: 14px;
  color: #9ca3af;
}

/* ===== 动画 ===== */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

/* ===== 滚动条 ===== */
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: #374151;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #4b5563;
}

/* ===== 响应式 ===== */
@media (max-width: 1200px) {
  .capital-overview {
    grid-template-columns: repeat(2, 1fr);
  }
  .risk-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .data-sections {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .strategy-sidebar {
    width: 100%;
    position: absolute;
    z-index: 50;
  }
  .strategy-sidebar.collapsed {
    width: 0;
    overflow: hidden;
  }
  .capital-overview {
    grid-template-columns: 1fr;
  }
  .risk-grid {
    grid-template-columns: 1fr;
  }
}

/* ===== 全局动画类 ===== */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.2);
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeIn {
  animation: fadeIn 0.5s ease-out;
}

.animate-slideDown {
  animation: slideDown 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.animate-scaleIn {
  animation: scaleIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.animate-pulse {
  animation: pulse 2s ease-in-out infinite;
}
</style>